import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { ProductCard } from "./ProductCard";
import { ShoppingBag, Truck, RotateCcw, MapPin, ArrowRight } from "lucide-react";

export default function MarketplaceHome() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/marketplace/home").then(({ data }) => setData(data)).catch(() => setData({ featured: [], new_arrivals: [], collections: [] })); }, []);

  return (
    <PublicLayout>
      <div data-testid="marketplace-home">
        {/* Hero */}
        <section className="relative bg-[#0A192F] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E63946 0, transparent 40%)" }} />
          <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24">
            <div className="flex items-center gap-2 text-[#E63946] label-overline"><ShoppingBag className="w-4 h-4" /> CBR Marketplace</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-3 max-w-3xl">Rep CBR Nation.<br />Wear the Program.</h1>
            <p className="text-white/70 mt-4 max-w-xl text-base">Official CBR-owned apparel and accessories for players, parents and supporters. Every item ships direct.</p>
            <Link to="/marketplace/products" data-testid="hero-shop-all" className="inline-flex items-center gap-2 mt-6 bg-[#E63946] hover:bg-[#c62f3b] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">Shop All <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-b border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-3 gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-2 justify-center"><Truck className="w-4 h-4 text-[#E63946]" /> Ships across Canada &amp; US</div>
            <div className="flex items-center gap-2 justify-center"><MapPin className="w-4 h-4 text-[#E63946]" /> Local pickup available</div>
            <div className="flex items-center gap-2 justify-center"><RotateCcw className="w-4 h-4 text-[#E63946]" /> Easy returns</div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-10 space-y-12">
          {!data ? <div className="text-slate-400">Loading…</div> : (
            <>
              {data.featured.length > 0 && <Row title="Featured" testid="row-featured" products={data.featured} />}
              {data.new_arrivals.length > 0 && <Row title="New Arrivals" testid="row-new" products={data.new_arrivals} />}

              {/* Collections */}
              <section data-testid="collections-grid">
                <h2 className="font-display text-2xl uppercase tracking-tight">Shop by Collection</h2>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.collections.map((c) => (
                    <Link key={c.id} to={`/marketplace/collection/${c.slug}`} data-testid={`collection-tile-${c.slug}`}
                      className="relative aspect-[16/9] rounded-sm overflow-hidden bg-[#0A192F] group">
                      {c.hero_image_url && <img src={normalizeImageUrl(c.hero_image_url)} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-300" />}
                      <div className="absolute inset-0 flex items-end p-4"><span className="font-display text-lg uppercase tracking-tight text-white">{c.name}</span></div>
                    </Link>
                  ))}
                </div>
              </section>

              {data.featured.length === 0 && data.new_arrivals.length === 0 && (
                <div className="text-center py-16 text-slate-400" data-testid="store-empty">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-semibold">The store is being stocked.</p>
                  <p className="text-sm">Check back soon for official CBR gear.</p>
                </div>
              )}

              {/* Policies */}
              <section className="border-t border-slate-200 pt-8 grid sm:grid-cols-3 gap-4 text-sm">
                <Link to="/marketplace/products" className="font-semibold text-[#0A192F] hover:text-[#E63946]">Shop All Products →</Link>
                <div className="text-slate-500">Shipping across Canada &amp; the US. Local pickup at checkout.</div>
                <div className="text-slate-500">Questions? Contact CBR support for order help &amp; returns.</div>
              </section>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

function Row({ title, products, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl uppercase tracking-tight">{title}</h2>
        <Link to="/marketplace/products" className="text-xs font-semibold uppercase tracking-wider text-[#E63946]">View all</Link>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {products.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}
